import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { ACCENT_HEX } from "@/lib/theme";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { TIPOS_TRABAJO } from "@/components/common/ServicePicker";
import { Switch } from "@/components/ui/switch";
import { TemplatesTab } from "@/features/messaging/TemplatesTab";
import { DataTab } from "@/features/settings/DataTab";
import { AccountingTab } from "@/features/settings/AccountingTab";
import { IntegrationsTab } from "@/features/settings/IntegrationsTab";   // Fix P
import { RatesTab } from "@/features/settings/RatesTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const Field = ({ label, children }) => (
  <div>
    <Label className="mb-1.5 block text-[12px] text-ink-tertiary">{label}</Label>
    {children}
  </div>
);

const Card = ({ title, children }) => (
  <section className="rounded-[8px] border border-line bg-white p-4">
    <h2 className="mb-3 text-[13px] font-semibold text-ink">{title}</h2>
    {children}
  </section>
);

export default function SettingsPage() {
  const { t, lang, setLang } = useT();
  const { user, can, patchUser } = useAuth();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const canEdit = can("settings", "manage");

  // Fix P: también ?tab=integrations
  const [tab, setTab] = useState(
    ["profile", "accounting", "integrations"].includes(params.get("tab")) ? params.get("tab") : "general"
  );
  const [draft, setDraft] = useState(null);
  const [profile, setProfile] = useState({ nombre: "", telefono: "", idioma: lang, timezone: "" });
  const [stDialog, setStDialog] = useState(false);
  const [stForm, setStForm] = useState({ nombre: "", categoria: "general", color: ACCENT_HEX, tiempo_estimado_horas: 2, tipo_trabajo: "turnover" });
  // Fix I (2026-09-04): cambio de la propia contraseña y restablecimiento de la de otro.
  const [pwd, setPwd] = useState({ actual: "", nueva: "", repetir: "" });
  const [resetResult, setResetResult] = useState(null);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    enabled: can("settings", "read"),
  });

  const { data: serviceTypes } = useQuery({
    queryKey: ["service-types"],
    queryFn: async () => (await api.get("/service-types")).data,
  });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: can("users", "read"),
  });

  useEffect(() => {
    const qbo = params.get("qbo");
    if (qbo === "connected") toast.success(t("settings.accounting.connected"));
    if (qbo === "error") toast.error(t("settings.accounting.oauth_error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settings) setDraft(structuredClone(settings));
  }, [settings]);

  useEffect(() => {
    if (user)
      setProfile({
        nombre: user.nombre || "",
        telefono: user.telefono || "",
        idioma: user.idioma || "es",
        timezone: user.timezone || "America/Los_Angeles",
      });
  }, [user]);

  const saveSettings = useMutation({
    mutationFn: async () =>
      (
        await api.patch("/settings", {
          vencimiento_horas: draft.vencimiento_horas,
          notificacion_cliente: draft.notificacion_cliente,
          textos_notificacion: draft.textos_notificacion,
          torre_control: draft.torre_control,
          marcas: draft.marcas,
          proyectos: draft.proyectos,
          finanzas: draft.finanzas,
          modo_piloto: !!draft.modo_piloto,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const saveProfile = useMutation({
    mutationFn: async () => (await api.patch("/auth/me", profile)).data,
    onSuccess: (data) => {
      patchUser(data);
      setLang(data.idioma || "es");
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const createServiceType = useMutation({
    mutationFn: async () =>
      (
        await api.post("/service-types", {
          ...stForm,
          tiempo_estimado_horas: Number(stForm.tiempo_estimado_horas),
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-types"] });
      toast.success(t("app.created"));
      setStDialog(false);
      setStForm({ nombre: "", categoria: "general", color: ACCENT_HEX, tiempo_estimado_horas: 2, tipo_trabajo: "turnover" });
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const togglePreinspeccion = useMutation({
    mutationFn: async ({ id, value }) => (await api.patch(`/service-types/${id}`, { requiere_preinspeccion: value })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-types"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  // Fix K (2026-09-28): la migración deja todo el catálogo como turnover; desde la tabla se
  // reclasifica cada servicio como proyecto o remediation.
  const changeTipoTrabajo = useMutation({
    mutationFn: async ({ id, value }) => (await api.patch(`/service-types/${id}`, { tipo_trabajo: value })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-types"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  // Fix I (2026-09-04): el propio usuario cambia su clave dando la actual.
  const changePassword = useMutation({
    mutationFn: async () =>
      (await api.post("/auth/change-password", {
        password_actual: pwd.actual,
        password_nueva: pwd.nueva,
      })).data,
    onSuccess: () => {
      setPwd({ actual: "", nueva: "", repetir: "" });
      toast.success(t("password.changed"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  // Fix I (2026-09-04): la clave temporal vuelve UNA sola vez en la respuesta; se muestra
  // en pantalla para copiarla y no se guarda en ningún lado.
  const resetPassword = useMutation({
    mutationFn: async (userId) => (await api.post(`/users/${userId}/reset-password`, {})).data,
    onSuccess: (data) => {
      setResetResult(data);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  return (
    <>
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} testId="settings-header" />

      <div className="flex-1 overflow-y-auto p-6" data-testid="settings-page">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 h-9 rounded-[6px] bg-white">
            {can("settings", "read") && (
              <>
                <TabsTrigger value="general" data-testid="tab-settings-general" className="text-[13px]">
                  {t("settings.tabs.general")}
                </TabsTrigger>
                <TabsTrigger value="brands" data-testid="tab-settings-brands" className="text-[13px]">
                  {t("settings.tabs.brands")}
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="service_types" data-testid="tab-settings-service-types" className="text-[13px]">
              {t("settings.tabs.service_types")}
            </TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-settings-rates" className="text-[13px]">
              {t("settings.tabs.rates")}
            </TabsTrigger>
            {can("users", "read") && (
              <TabsTrigger value="users" data-testid="tab-settings-users" className="text-[13px]">
                {t("settings.tabs.users")}
              </TabsTrigger>
            )}
            {can("messaging", "read") && (
              <TabsTrigger value="templates" data-testid="tab-settings-templates" className="text-[13px]">
                {t("settings.tabs.templates")}
              </TabsTrigger>
            )}
            {can("control_tower", "read") && (
              <TabsTrigger value="alerts" data-testid="tab-settings-alerts" className="text-[13px]">
                {t("settings.tabs.alerts")}
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" data-testid="tab-settings-profile" className="text-[13px]">
              {t("settings.tabs.profile")}
            </TabsTrigger>
            {user?.rol === "owner" && (
              <TabsTrigger value="data" data-testid="tab-settings-data" className="text-[13px]">
                {t("settings.tabs.data")}
              </TabsTrigger>
            )}
            {user?.rol === "owner" && (
              <TabsTrigger value="accounting" data-testid="tab-settings-accounting" className="text-[13px]">
                {t("settings.tabs.accounting")}
              </TabsTrigger>
            )}
            {user?.rol === "owner" && (
              <TabsTrigger value="integrations" data-testid="tab-settings-integrations" className="text-[13px]">
                {t("settings.tabs.integrations")}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="general">
            {!canEdit && (
              <p className="mb-3 rounded-[6px] border border-state-warn-soft bg-state-warn-soft px-3 py-2 text-[12.5px] text-state-warn">
                {t("settings.readonly")}
              </p>
            )}
            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t("settings.due_hours")}>
                <div className="grid grid-cols-2 gap-3">
                  {["urgente", "alta", "media", "baja"].map((p) => (
                    <Field key={p} label={t(`priority.${p}`)}>
                      <Input
                        data-testid={`due-hours-${p}`}
                        type="number"
                        disabled={!canEdit}
                        value={draft?.vencimiento_horas?.[p] ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            vencimiento_horas: { ...d.vencimiento_horas, [p]: Number(e.target.value) },
                          }))
                        }
                        className="h-9 rounded-[6px] text-[13.5px]"
                      />
                    </Field>
                  ))}
                </div>
              </Card>
              {/* Fix N (2026-09-28) */}
              <Card title={t("settings.pilot.title")}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[12.5px] text-ink-secondary">{t("settings.pilot.desc")}</p>
                  <Switch data-testid="settings-pilot-mode" disabled={!canEdit}
                    checked={!!draft?.modo_piloto}
                    onCheckedChange={(v) => setDraft((d) => ({ ...d, modo_piloto: v }))} />
                </div>
                <p className="mt-2 text-[11.5px] text-ink-tertiary">{t("settings.pilot.hint")}</p>
              </Card>
              <Card title={t("settings.notify")}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("properties.notify_channel")}>
                    <Input
                      data-testid="notify-channel-default"
                      disabled={!canEdit}
                      value={draft?.notificacion_cliente?.canal_default || ""}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          notificacion_cliente: { ...d.notificacion_cliente, canal_default: e.target.value },
                        }))
                      }
                      className="h-9 rounded-[6px] text-[13.5px]"
                    />
                  </Field>
                  <Field label={t("properties.notify_interval")}>
                    <Input
                      data-testid="notify-interval-default"
                      type="number"
                      disabled={!canEdit}
                      value={draft?.notificacion_cliente?.intervalo_dias_default ?? ""}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          notificacion_cliente: {
                            ...d.notificacion_cliente,
                            intervalo_dias_default: Number(e.target.value),
                          },
                        }))
                      }
                      className="h-9 rounded-[6px] text-[13.5px]"
                    />
                  </Field>
                </div>
              </Card>
              <Card title={t("settings.projects.title")}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("settings.projects.dias_sin_avance")}>
                    <Input
                      data-testid="projects-alert-days-input"
                      type="number"
                      disabled={!canEdit}
                      value={draft?.proyectos?.dias_sin_avance_alerta ?? ""}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          proyectos: { ...d.proyectos, dias_sin_avance_alerta: Number(e.target.value) },
                        }))
                      }
                      className="h-9 rounded-[6px] text-[13.5px]"
                    />
                  </Field>
                </div>
              </Card>
              <Card title={t("settings.finance.title")}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("settings.finance.corte_exige")}>
                    <Select
                      disabled={!canEdit}
                      value={draft?.finanzas?.corte_exige_factura ?? "enviada"}
                      onValueChange={(v) =>
                        setDraft((d) => ({ ...d, finanzas: { ...d.finanzas, corte_exige_factura: v } }))
                      }
                    >
                      <SelectTrigger data-testid="finance-corte-exige-select" className="h-9 rounded-[6px] text-[13.5px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enviada">{t("settings.finance.corte_exige_enviada")}</SelectItem>
                        <SelectItem value="emitida">{t("settings.finance.corte_exige_emitida")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </Card>
              <div className="lg:col-span-2">
                <Card title={t("settings.texts")}>
                  <div className="space-y-3">
                    {Object.keys(draft?.textos_notificacion || {}).map((k) => (
                      <Field key={k} label={k}>
                        <Textarea
                          data-testid={`text-${k}`}
                          disabled={!canEdit}
                          value={draft.textos_notificacion[k]}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              textos_notificacion: { ...d.textos_notificacion, [k]: e.target.value },
                            }))
                          }
                          className="min-h-[56px] rounded-[6px] text-[13.5px]"
                        />
                      </Field>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
            {canEdit && (
              <Button
                data-testid="settings-save-button"
                onClick={() => saveSettings.mutate()}
                className="mt-4 h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
              >
                {t("app.save")}
              </Button>
            )}
          </TabsContent>

          <TabsContent value="brands">
            <div className="grid gap-4 lg:grid-cols-2">
              {Object.entries(draft?.marcas || {}).map(([key, marca]) => (
                <Card key={key} title={key === "bb" ? "B&B" : "Betos"}>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ["nombre", t("settings.brand_name")],
                      ["nombre_legal", t("settings.brand_legal")],
                      ["email", t("settings.brand_email")],
                      ["telefono", t("settings.brand_phone")],
                      ["color", t("settings.brand_color")],
                    ].map(([field, label]) => (
                      <Field key={field} label={label}>
                        <Input
                          data-testid={`brand-${key}-${field}`}
                          disabled={!canEdit}
                          value={marca[field] || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              marcas: { ...d.marcas, [key]: { ...d.marcas[key], [field]: e.target.value } },
                            }))
                          }
                          className="h-9 rounded-[6px] text-[13.5px]"
                        />
                      </Field>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
            {canEdit && (
              <Button
                data-testid="brands-save-button"
                onClick={() => saveSettings.mutate()}
                className="mt-4 h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
              >
                {t("app.save")}
              </Button>
            )}
          </TabsContent>

          <TabsContent value="service_types">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-[13px] text-ink-tertiary">{t("serviceTypes.subtitle")}</p>
              {can("service_types", "create") && (
                <Button
                  data-testid="new-service-type-button"
                  onClick={() => setStDialog(true)}
                  className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {t("serviceTypes.new")}
                </Button>
              )}
            </div>
            <div className="overflow-hidden rounded-[8px] border border-line bg-white">
              {serviceTypes?.items?.length ? (
                <table className="w-full text-[13px]" data-testid="service-types-table">
                  <thead className="bg-surface-sunken">
                    <tr className="border-b border-line text-[11.5px] uppercase tracking-[0.04em] text-ink-tertiary">
                      <th className="px-3 py-2 text-left">{t("serviceTypes.name")}</th>
                      <th className="px-3 py-2 text-left">{t("serviceTypes.work_type")}</th>
                      <th className="px-3 py-2 text-left">{t("serviceTypes.category")}</th>
                      <th className="px-3 py-2 text-left">{t("serviceTypes.color")}</th>
                      <th className="px-3 py-2 text-right">{t("serviceTypes.hours")}</th>
                      <th className="px-3 py-2 text-left">{t("serviceTypes.requires_preinspection")}</th>
                      <th className="px-3 py-2 text-left">{t("app.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceTypes.items.map((s) => (
                      <tr key={s.id} data-testid={`service-type-${s.id}`} className="h-[44px] border-b border-line last:border-0">
                        <td className="px-3 font-medium">{s.nombre}</td>
                        <td className="px-3">
                          <select
                            data-testid={`service-type-work-type-${s.id}`}
                            value={s.tipo_trabajo || "turnover"}
                            disabled={!can("service_types", "update")}
                            onChange={(e) => changeTipoTrabajo.mutate({ id: s.id, value: e.target.value })}
                            className="h-8 rounded-[6px] border border-line bg-white px-2 text-[12.5px]"
                          >
                            {TIPOS_TRABAJO.map((tp) => (
                              <option key={tp} value={tp}>{t(`wo.form.work_types.${tp}`)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 text-ink-tertiary">{s.categoria}</td>
                        <td className="px-3">
                          <span className="inline-flex items-center gap-2">
                            <span className="h-3 w-3 rounded-[3px]" style={{ backgroundColor: s.color }} />
                            <span className="text-[12px] text-ink-tertiary">{s.color}</span>
                          </span>
                        </td>
                        <td className="px-3 text-right tabular-nums">{s.tiempo_estimado_horas}</td>
                        <td className="px-3">
                          <Switch
                            data-testid={`service-type-preinspection-${s.id}`}
                            checked={!!s.requiere_preinspeccion}
                            disabled={!can("service_types", "update")}
                            onCheckedChange={(v) => togglePreinspeccion.mutate({ id: s.id, value: v })}
                          />
                        </td>
                        <td className="px-3">
                          <StatusChip tone={statusTone(s.status)} label={t(`status.${s.status}`)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyState
                  icon={Wrench}
                  testId="service-types-empty"
                  title={t("serviceTypes.empty_title")}
                  description={t("serviceTypes.empty_desc")}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="rates">
            <RatesTab />
          </TabsContent>

          <TabsContent value="users">
            <div className="overflow-hidden rounded-[8px] border border-line bg-white">
              <table className="w-full text-[13px]" data-testid="users-table">
                <thead className="bg-surface-sunken">
                  <tr className="border-b border-line text-[11.5px] uppercase tracking-[0.04em] text-ink-tertiary">
                    <th className="px-3 py-2 text-left">{t("app.name")}</th>
                    <th className="px-3 py-2 text-left">{t("app.email")}</th>
                    <th className="px-3 py-2 text-left">{t("settings.role")}</th>
                    <th className="px-3 py-2 text-left">{t("settings.last_login")}</th>
                    <th className="px-3 py-2 text-left">{t("app.status")}</th>
                    <th className="px-3 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {(users?.items || []).map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="h-[44px] border-b border-line last:border-0">
                      <td className="px-3 font-medium">{u.nombre}</td>
                      <td className="px-3 text-ink-tertiary">{u.email}</td>
                      <td className="px-3">{t(`roles.${u.rol}`)}</td>
                      <td className="px-3 text-ink-tertiary">{u.last_login_at ? formatDateTime(u.last_login_at) : "—"}</td>
                      <td className="px-3">
                        <StatusChip tone={statusTone(u.status)} label={t(`status.${u.status}`)} />
                        {u.must_change_password && (
                          <span className="ml-2 text-[11.5px] text-ink-tertiary">
                            {t("password.pending")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 text-right">
                        {can("users", "update") && (
                          <Button
                            variant="outline"
                            data-testid={`user-reset-${u.id}`}
                            disabled={resetPassword.isPending}
                            onClick={() => resetPassword.mutate(u.id)}
                            className="h-7 rounded-[6px] text-[12px]"
                          >
                            {t("password.reset")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Fix I (2026-09-04): la clave temporal se muestra una sola vez, acá. */}
            <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
              <DialogContent data-testid="reset-password-dialog">
                <DialogHeader>
                  <DialogTitle>{t("password.reset_done_title")}</DialogTitle>
                </DialogHeader>
                <p className="text-[13px] text-ink-tertiary">
                  {t("password.reset_done_desc", { email: resetResult?.email || "" })}
                </p>
                <p
                  data-testid="reset-password-value"
                  className="select-all rounded-[6px] border border-line bg-surface-sunken px-3 py-2 font-mono text-[15px]"
                >
                  {resetResult?.password_temporal}
                </p>
                <DialogFooter>
                  <Button
                    onClick={() => setResetResult(null)}
                    className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
                  >
                    {t("app.close")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="templates">
            <TemplatesTab />
          </TabsContent>

          <TabsContent value="alerts">
            <div className="grid gap-4 lg:grid-cols-2" data-testid="alerts-settings">
              <Card title={t("settings.alerts.confirm")}>
                <div className="grid grid-cols-2 gap-3">
                  {["urgente", "alta", "media", "baja"].map((p) => (
                    <Field key={p} label={t(`priority.${p}`)}>
                      <Input
                        data-testid={`alert-confirm-${p}`}
                        type="number"
                        disabled={!canEdit}
                        value={draft?.torre_control?.confirmacion_horas?.[p] ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            torre_control: {
                              ...d.torre_control,
                              confirmacion_horas: {
                                ...(d.torre_control?.confirmacion_horas || {}),
                                [p]: Number(e.target.value),
                              },
                            },
                          }))
                        }
                        className="h-9 rounded-[6px] text-[13.5px]"
                      />
                    </Field>
                  ))}
                </div>
              </Card>
              <Card title={t("settings.alerts.windows")}>
                <div className="grid grid-cols-2 gap-3">
                  {["evidencia_inicial_horas", "correccion_horas", "vencida_sin_update_horas",
                    "aprobacion_horas", "adicional_horas", "sin_facturar_horas",
                    "sin_asignar_horas", "llegada_tolerancia_horas", "intake_sin_orden_horas"].map((k) => (
                    <Field key={k} label={t(`settings.alerts.${k}`)}>
                      <Input
                        data-testid={`alert-${k}`}
                        type="number"
                        disabled={!canEdit}
                        value={draft?.torre_control?.[k] ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            torre_control: { ...d.torre_control, [k]: Number(e.target.value) },
                          }))
                        }
                        className="h-9 rounded-[6px] text-[13.5px]"
                      />
                    </Field>
                  ))}
                </div>
              </Card>
              <Card title={t("settings.alerts.escalation")}>
                <div className="grid grid-cols-2 gap-3">
                  {["paso2", "paso3"].map((k) => (
                    <Field key={k} label={t(`settings.alerts.${k}`)}>
                      <Input
                        data-testid={`alert-escalation-${k}`}
                        type="number"
                        disabled={!canEdit}
                        value={draft?.torre_control?.escalado_minutos?.[k] ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            torre_control: {
                              ...d.torre_control,
                              escalado_minutos: {
                                ...(d.torre_control?.escalado_minutos || {}),
                                [k]: Number(e.target.value),
                              },
                            },
                          }))
                        }
                        className="h-9 rounded-[6px] text-[13.5px]"
                      />
                    </Field>
                  ))}
                </div>
                <p className="mt-2 text-[11.5px] text-ink-tertiary">{t("settings.alerts.escalation_help")}</p>
              </Card>
            </div>
            {canEdit && (
              <Button
                data-testid="alerts-save-button"
                onClick={() => saveSettings.mutate()}
                className="mt-4 h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
              >
                {t("app.save")}
              </Button>
            )}
          </TabsContent>

          <TabsContent value="profile">
            <div className="max-w-[520px]">
              <Card title={t("settings.tabs.profile")}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("app.name")}>
                    <Input
                      data-testid="profile-name-input"
                      value={profile.nombre}
                      onChange={(e) => setProfile((p) => ({ ...p, nombre: e.target.value }))}
                      className="h-9 rounded-[6px] text-[13.5px]"
                    />
                  </Field>
                  <Field label={t("app.phone")}>
                    <Input
                      data-testid="profile-phone-input"
                      value={profile.telefono || ""}
                      onChange={(e) => setProfile((p) => ({ ...p, telefono: e.target.value }))}
                      className="h-9 rounded-[6px] text-[13.5px]"
                    />
                  </Field>
                  <Field label={t("app.language")}>
                    <Select value={profile.idioma} onValueChange={(v) => setProfile((p) => ({ ...p, idioma: v }))}>
                      <SelectTrigger data-testid="profile-language-select" className="h-9 rounded-[6px] text-[13.5px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="es">Español</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t("app.timezone")}>
                    <Select value={profile.timezone} onValueChange={(v) => setProfile((p) => ({ ...p, timezone: v }))}>
                      <SelectTrigger data-testid="profile-timezone-select" className="h-9 rounded-[6px] text-[13.5px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                        <SelectItem value="America/Bogota">America/Bogota</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Button
                  data-testid="profile-save-button"
                  onClick={() => saveProfile.mutate()}
                  className="mt-4 h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
                >
                  {t("app.save")}
                </Button>
              </Card>

              {/* Fix I (2026-09-04): antes no había ninguna forma de cambiar la contraseña. */}
              <div className="mt-4">
                <Card title={t("password.title")}>
                  <div className="space-y-3">
                    <Field label={t("password.current")}>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        data-testid="password-current-input"
                        value={pwd.actual}
                        onChange={(e) => setPwd((p) => ({ ...p, actual: e.target.value }))}
                        className="h-9 rounded-[6px] text-[13.5px]"
                      />
                    </Field>
                    <Field label={t("password.new")}>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        data-testid="password-new-input"
                        value={pwd.nueva}
                        onChange={(e) => setPwd((p) => ({ ...p, nueva: e.target.value }))}
                        className="h-9 rounded-[6px] text-[13.5px]"
                      />
                    </Field>
                    <Field label={t("password.repeat")}>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        data-testid="password-repeat-input"
                        value={pwd.repetir}
                        onChange={(e) => setPwd((p) => ({ ...p, repetir: e.target.value }))}
                        className="h-9 rounded-[6px] text-[13.5px]"
                      />
                    </Field>
                  </div>
                  <p className="mt-3 text-[12px] text-ink-tertiary">{t("password.rules")}</p>
                  <Button
                    data-testid="password-save-button"
                    disabled={
                      changePassword.isPending ||
                      !pwd.actual ||
                      !pwd.nueva ||
                      pwd.nueva !== pwd.repetir
                    }
                    onClick={() => changePassword.mutate()}
                    className="mt-4 h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
                  >
                    {t("password.save")}
                  </Button>
                </Card>
              </div>
            </div>
          </TabsContent>

          {user?.rol === "owner" && (
            <TabsContent value="data">
              <DataTab />
            </TabsContent>
          )}
          {user?.rol === "owner" && (
            <TabsContent value="accounting">
              <AccountingTab />
            </TabsContent>
          )}
          {user?.rol === "owner" && (
            <TabsContent value="integrations">
              <IntegrationsTab />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <Dialog open={stDialog} onOpenChange={setStDialog}>
        <DialogContent data-testid="service-type-dialog" className="max-w-[460px] rounded-[8px]">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("serviceTypes.new")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3.5">
            <div className="col-span-2">
              <Field label={t("serviceTypes.name")}>
                <Input
                  data-testid="service-type-name-input"
                  value={stForm.nombre}
                  onChange={(e) => setStForm((f) => ({ ...f, nombre: e.target.value }))}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
            </div>
            <Field label={t("serviceTypes.work_type")}>
              <select
                data-testid="service-type-work-type-input"
                value={stForm.tipo_trabajo}
                onChange={(e) => setStForm((f) => ({ ...f, tipo_trabajo: e.target.value }))}
                className="h-9 w-full rounded-[6px] border border-line bg-white px-2 text-[13.5px]"
              >
                {TIPOS_TRABAJO.map((tp) => (
                  <option key={tp} value={tp}>{t(`wo.form.work_types.${tp}`)}</option>
                ))}
              </select>
            </Field>
            <Field label={t("serviceTypes.category")}>
              <Input
                data-testid="service-type-category-input"
                value={stForm.categoria}
                onChange={(e) => setStForm((f) => ({ ...f, categoria: e.target.value }))}
                className="h-9 rounded-[6px] text-[13.5px]"
              />
            </Field>
            <Field label={t("serviceTypes.hours")}>
              <Input
                data-testid="service-type-hours-input"
                type="number"
                value={stForm.tiempo_estimado_horas}
                onChange={(e) => setStForm((f) => ({ ...f, tiempo_estimado_horas: e.target.value }))}
                className="h-9 rounded-[6px] text-[13.5px]"
              />
            </Field>
            <Field label={t("serviceTypes.color")}>
              <Input
                data-testid="service-type-color-input"
                type="color"
                value={stForm.color}
                onChange={(e) => setStForm((f) => ({ ...f, color: e.target.value }))}
                className="h-9 w-20 rounded-[6px] p-1"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-9 rounded-[6px] text-[13px]" onClick={() => setStDialog(false)}>
              {t("app.cancel")}
            </Button>
            <Button
              data-testid="service-type-save-button"
              disabled={!stForm.nombre.trim() || createServiceType.isPending}
              onClick={() => createServiceType.mutate()}
              className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
            >
              {t("app.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
